import React from 'react';
import PropTypes from 'prop-types';
import styled, { css } from 'styled-components';

import {
  CertBackground,
  CertTemplate,
  CertInnerPattern,
} from '@lt/components/icons-react/gift_certificate';
import { formatCurrency } from '../../../utils/stringUtils';
import { CERTIFICATE_DESIGNS } from '../store/certificate/constants';

import { isSSR } from '../../../helpers/ssr';

const Certificate = ({ width, value, holder, text, design, className }) => (
  <StyledCertificate className={className} width={width} design={design}>
    <CertificatePicture />
    <CertContentContainer>
      <CertContentWrapper>
        <CertificatePattern />
        <CertContent>
          <CertificateTemplate />
          <InnerWrapper>
            <svg viewBox="0 0 240 240">
              <StyledCertificateValue x="0" y="25" height="20%">
                {value ? formatCurrency(value) : null}
              </StyledCertificateValue>
              <StyledCertificateHolder x="0" y="100" height="15%">
                {holder}
              </StyledCertificateHolder>
              <StyledCertificateText x="0" y="135" height="35%">
                {text}
              </StyledCertificateText>
            </svg>
          </InnerWrapper>
        </CertContent>
      </CertContentWrapper>
    </CertContentContainer>
  </StyledCertificate>
);

Certificate.propTypes = {
  className: PropTypes.string,
  holder: PropTypes.string,
  text: PropTypes.string,
  value: PropTypes.number,
  width: PropTypes.number,
  design: PropTypes.shape({
    background: PropTypes.string.isRequired,
    picture: PropTypes.string.isRequired,
    ornament: PropTypes.string.isRequired,
    amount: PropTypes.string.isRequired,
  }),
};
Certificate.defaultProps = {
  className: '',
  holder: '',
  text: '',
  value: null,
  width: 260,
  design: CERTIFICATE_DESIGNS[Object.keys(CERTIFICATE_DESIGNS)[0]],
};

const StyledCertificateLabel = styled.foreignObject.attrs({
  width: '100%',
})`
  text-align: center;
  text-anchor: middle;
  overflow-wrap: break-word;
`;

const StyledCertificateValue = styled(StyledCertificateLabel)`
  font-size: 38px;
  line-height: 1;
  font-weight: 500;
`;

const StyledCertificateHolder = styled(StyledCertificateLabel)`
  font-size: 14px;
  line-height: 1;
  font-weight: 500;
`;

const StyledCertificateText = styled(StyledCertificateLabel)`
  font-size: 10px;
  line-height: 1.1;
`;

const commonSvgStyles = css`
  position: absolute;
  display: flex;
  width: 100%;
  height: 100%;
  justify-content: center;
  align-items: center;
`;

const CertificatePicture = styled(CertBackground)`
  ${commonSvgStyles}

  width: 92%;
  stroke-width: 0;
`;

const CertificateTemplate = styled(CertTemplate)`
  ${commonSvgStyles}

  margin-bottom: 9%;
  width: 64%;
`;

const CertificatePattern = styled(CertInnerPattern)`
  ${commonSvgStyles}

  width: 86%;

  ${isSSR && 'padding-top: 3px'}
`;

const CertContentWrapper = styled.div`
  position: relative;
  padding: 5.5%;
  display: flex;
  box-sizing: border-box;
  width: 100%;
  height: 100%;
  justify-content: center;
  align-items: center;
  user-select: none;
`;

const CertContent = styled.div`
  display: flex;
  width: 100%;
  height: 100%;
  justify-content: center;
  align-items: center;
  background: ${(props) => props.theme.colors.white};
`;

const CertContentContainer = styled.div`
  position: relative;
  padding: 3.5%;
  display: flex;
  box-sizing: border-box;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  background: ${(props) => props.theme.colors.white};
`;

const InnerWrapper = styled.div`
  position: absolute;
  width: 75%;
  height: 50%;
`;

const StyledCertificate = styled.div`
  --height: ${(props) => `${Math.sqrt(2) * props.width}px`};

  position: relative;
  display: flex;
  width: ${(props) => `${props.width}px`};
  height: var(--height);
  justify-content: center;
  align-items: center;
  background-color: ${(props) => props.design.background};
  will-change: contents;

  ${CertContentContainer} {
    --content-scale: 0.62;

    /* slightly differs from A$ :( */
    --content-height-to-width-ratio: 1.39;
    --content-width: calc(
      ${(props) => `${props.width}px`} * var(--content-scale)
    );

    width: var(--content-width);
    height: calc(var(--content-width) * var(--content-height-to-width-ratio));
  }

  ${CertificatePicture} {
    path {
      stroke: ${(props) => props.design.picture};
      fill: ${(props) => props.design.picture};
    }
  }

  ${CertificatePattern} {
    path {
      stroke: ${(props) => props.design.ornament};
    }
  }

  ${CertContentWrapper} {
    background: ${(props) => props.design.ornament};
  }

  ${StyledCertificateValue} {
    color: ${(props) => props.design.amount};
  }
`;

export default React.memo(Certificate);
